import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../lib/apiClient";
import { useApp } from "../lib/store";
import { PageHeader, GradeBadge, Avatar, Empty } from "../components/Shared";
import { useEmployees, EmployeePicker, Panel, Segmented } from "../components/Filters";
import { scoreColor } from "../lib/constants";

export default function Quarterly() {
  const { year, can, isEmployee, selfEmployeeId } = useApp();
  const employees = useEmployees();
  const [empId, setEmpId] = useState(isEmployee ? selfEmployeeId : "om");
  const [quarter, setQuarter] = useState(3);
  const [data, setData] = useState(null);

  useEffect(() => { if (empId) api.quarterly(empId, quarter, year).then(setData).catch(() => {}); }, [empId, quarter, year]);

  const TrendIcon = data?.trend === "up" ? TrendingUp : data?.trend === "down" ? TrendingDown : Minus;
  const trendColor = data?.trend === "up" ? "#10B981" : data?.trend === "down" ? "#EF4444" : "#64748B";
  const chartData = data?.months.map((m) => ({ month: m.month_name.slice(0, 3), score: m.score || 0 })) || [];

  return (
    <div>
      <PageHeader title="Quarterly Review" subtitle="Rolling average of 3 monthly scores with trend analysis"
        exportModule="quarterly" exportParams={{ employee_id: empId, month: (quarter - 1) * 3 + 1, year }}>
        <EmployeePicker value={empId} onChange={setEmpId} employees={employees} disabled={isEmployee} testid="quarterly-employee-picker" />
      </PageHeader>

      <div className="mb-5"><Segmented options={[{ v: 1, n: "Q1" }, { v: 2, n: "Q2" }, { v: 3, n: "Q3" }, { v: 4, n: "Q4" }]} value={quarter} onChange={setQuarter} testidPrefix="quarter-seg" /></div>

      {!data ? <div className="py-20 text-center text-muted-c">Loading…</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-4">
            <Panel testid="quarterly-summary">
              <div className="flex items-center gap-3 mb-4"><Avatar src={data.employee.profile_image} name={data.employee.name} size={48} /><div><p className="font-display font-bold">{data.employee.name}</p><p className="text-sm text-secondary">{data.employee.designation}</p></div></div>
              <div className="elevated rounded-xl p-5 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-c mb-1">Quarterly Score</p>
                <p className="mono font-bold text-4xl" style={{ color: scoreColor(data.quarterly_score) }}>{data.quarterly_score}</p>
                <div className="mt-2 flex items-center justify-center gap-2"><GradeBadge grade={data.grade} size="lg" /><span className="pill px-2.5 py-0.5 text-xs" style={{ color: trendColor, borderColor: trendColor + "66" }}><TrendIcon size={13} /> {data.trend}</span></div>
              </div>
            </Panel>
            <Panel title="Month-wise Scores" testid="quarterly-months">
              {data.months.map((m) => (
                <div key={m.month} className="flex items-center justify-between py-2 border-b border-subtle last:border-0">
                  <span className="text-sm">{m.month_name}</span>
                  {m.score != null ? <span className="mono font-bold" style={{ color: scoreColor(m.score) }}>{m.score}</span> : <span className="text-xs text-muted-c">No review</span>}
                </div>
              ))}
            </Panel>
          </div>
          <div className="lg:col-span-8">
            <Panel title="Performance Trend" testid="quarterly-chart">
              {chartData.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 10 }} />
                    <Line type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={3} dot={{ r: 5, fill: "#2563EB" }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
